// Full 3D Mode — a standalone first/third-person 3D world, unrelated
// to the 2D sim. When active it takes over the canvas with its own
// WebGL scene: a flat dirt ground, a sky, and a sun for light. You
// play as the axolotl mascot:
//
//   - Arrow keys → move the axolotl. Up/Down walk forward/back along
//                  its facing; Left/Right turn it. The camera follows
//                  in third person.
//   - Drag       → orbit the camera around the axolotl (look around).
//   - Click      → place the currently-selected material at the spot
//                  under the cursor, rendered as a realistic 3D block
//                  (translucent water, glowing fire, rough dirt, …).
//   - Wheel      → dolly the follow-camera in / out.
//
// Placement snaps to a 1-unit grid and stacks: clicking the top of an
// existing block drops the next one on top, so you can build.

import { useEffect, useRef } from "react";
import {
  AmbientLight,
  AnimationMixer,
  BoxGeometry,
  Box3,
  Clock,
  Color,
  DirectionalLight,
  Fog,
  GridHelper,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  Scene,
  SphereGeometry,
  Vector2,
  Vector3,
  WebGLRenderer,
  type AnimationAction,
  type AnimationClip,
  type Material,
  type Object3D,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { MASCOT_GLB_URL } from "../mascot/asset";
import reactionsYaml from "../../../../packages/material-db/data/reactions.yaml?raw";
import type { Shape3DKind } from "../sim/shapes3d";
import type { MaterialDef } from "../sim/types";
import { parseContactReactions } from "./full3d/reactions";

interface Full3DViewProps {
  active: boolean;
  /** The material the user will place on click. Read live through a
   * ref so changing the brush doesn't tear down the scene. */
  selectedMaterial: MaterialDef | null;
  /** Full material list — lets the reaction pass look up a product's
   * def (colour + shape) by id when it transforms a placed block. */
  materials: MaterialDef[];
  /** Resolve a material to its 3D shape keyword (block / grains /
   * liquid / gas / crystal / glow). Supplied by App from the loaded
   * material-3d.yaml table. */
  shapeFor: (def: MaterialDef | null) => Shape3DKind;
  /** Leave Full 3D Mode (the top-right exit button + Esc). */
  onExit: () => void;
}

// Contact-reaction rules, parsed once from the engine's reactions.yaml.
const CONTACT_RULES = parseContactReactions(reactionsYaml);

// The colour a material renders with in 3D — straight from its 2D
// palette colour, falling back to a neutral grey.
function colorOf(def: MaterialDef | null): Color {
  const [r, g, b] = def?.color ?? [170, 170, 175];
  return new Color(r / 255, g / 255, b / 255);
}

// Build the 3D object for one placed cell, given the material and the
// resolved shape keyword. Returns a single Object3D (Mesh or
// InstancedMesh — never a Group) so the placement raycaster can hit
// it with a non-recursive intersect. The object is centred on the
// cell origin; the caller positions it at (gx, h + 0.5, gz).
//
//   block   — a full solid cube, faintly metallic.
//   grains  — an InstancedMesh of many small jittered cubes: a heap
//             of grains (sand, soil, ash).
//   liquid  — a low glossy translucent slab that fills the bottom of
//             the cell, like a settled puddle.
//   gas     — a faint translucent sphere — a soft puff.
//   crystal — a faceted icosahedron gem.
//   glow    — an emissive, self-lit cube (fire, lava, plasma).
function buildPlacedObject(
  def: MaterialDef | null,
  kind: Shape3DKind,
): Mesh | InstancedMesh {
  const color = colorOf(def);
  switch (kind) {
    case "grains": {
      // Real-sand look: a dense field of tiny spherical grains packed
      // into a rounded heap (centre piled higher than edges, matching
      // the natural angle of repose) with a touch of per-grain
      // brightness jitter so the surface reads as scattered sand
      // rather than a checker pattern. Spheres instead of cubes kill
      // the visible cube faces that made the old 4×4×4 layout look
      // like a Rubik's cube; 12×12 grains across the cell makes each
      // one read as a tiny granule at normal viewing distance.
      const per = 12; // 12³ = 1728 candidate slots
      const grainSize = 1.0 / per;
      const g = new SphereGeometry(grainSize * 0.62, 6, 6);
      const m = new MeshStandardMaterial({ roughness: 0.97, metalness: 0.0 });
      // Heap profile: pyramid-with-rounded-corners. For each (x,z)
      // column, only the lowest `colHeight` slots get a grain — so the
      // centre column is full, the edges are short, and the surface
      // is a soft dome instead of a cube.
      const slots: { x: number; y: number; z: number; t: number }[] = [];
      for (let ix = 0; ix < per; ix++) {
        for (let iz = 0; iz < per; iz++) {
          // Normalised distance from cell centre in the horizontal plane.
          const fx = (ix + 0.5) / per - 0.5;
          const fz = (iz + 0.5) / per - 0.5;
          const r = Math.sqrt(fx * fx + fz * fz) / 0.5; // 0 at centre, 1 at corner
          // Heap falls off as a smooth dome, never below ~25% so a
          // single placed grain-block still reads as a small heap
          // rather than a thin disc.
          const heap = Math.max(0.25, 1 - r * r * 0.9);
          const colH = Math.max(1, Math.round(per * heap));
          for (let iy = 0; iy < colH; iy++) {
            // Sub-cell jitter so the pack isn't a visible lattice.
            const jx = (Math.random() - 0.5) * grainSize * 0.5;
            const jy = (Math.random() - 0.5) * grainSize * 0.5;
            const jz = (Math.random() - 0.5) * grainSize * 0.5;
            slots.push({
              x: fx + jx,
              y: (iy + 0.5) / per - 0.5 + jy,
              z: fz + jz,
              t: Math.random(),
            });
          }
        }
      }
      const inst = new InstancedMesh(g, m, slots.length);
      const mtx = new Matrix4();
      const tmpColor = new Color();
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i];
        mtx.makeTranslation(s.x, s.y, s.z);
        inst.setMatrixAt(i, mtx);
        // ±12 % brightness per grain so the surface speckles like sand.
        const v = 0.88 + s.t * 0.24;
        tmpColor.setRGB(color.r * v, color.g * v, color.b * v);
        inst.setColorAt(i, tmpColor);
      }
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      return inst;
    }
    case "liquid": {
      const g = new BoxGeometry(0.98, 0.55, 0.98);
      const m = new MeshStandardMaterial({
        color,
        transparent: true,
        opacity: 0.66,
        roughness: 0.06,
        metalness: 0.0,
      });
      const mesh = new Mesh(g, m);
      mesh.position.y = -0.22; // settle to the bottom of the cell
      return mesh;
    }
    case "gas": {
      const g = new SphereGeometry(0.5, 16, 12);
      const m = new MeshStandardMaterial({
        color,
        transparent: true,
        opacity: 0.28,
        roughness: 1.0,
      });
      return new Mesh(g, m);
    }
    case "crystal": {
      const g = new IcosahedronGeometry(0.62, 0);
      const m = new MeshStandardMaterial({
        color,
        transparent: true,
        opacity: 0.85,
        roughness: 0.1,
        metalness: 0.2,
        flatShading: true,
      });
      return new Mesh(g, m);
    }
    case "glow": {
      const g = new BoxGeometry(1, 1, 1);
      const m = new MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.9,
        roughness: 0.5,
      });
      return new Mesh(g, m);
    }
    case "block":
    default: {
      const g = new BoxGeometry(1, 1, 1);
      const m = new MeshStandardMaterial({
        color,
        roughness: 0.88,
        metalness: def?.phase === "solid" ? 0.12 : 0.0,
      });
      return new Mesh(g, m);
    }
  }
}

export function Full3DView(props: Full3DViewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Live brush material — read inside the placement closure so the
  // scene effect doesn't depend on it (changing the selected material
  // must not reload the axolotl GLB).
  const materialRef = useRef<MaterialDef | null>(props.selectedMaterial);
  materialRef.current = props.selectedMaterial;
  // Live shape-resolver + exit callback, same ref trick so the scene
  // effect never re-runs when they change identity.
  const shapeForRef = useRef(props.shapeFor);
  shapeForRef.current = props.shapeFor;
  const onExitRef = useRef(props.onExit);
  onExitRef.current = props.onExit;
  // id → MaterialDef lookup, so the reaction pass can build a product
  // block from its material id. Rebuilt only when the list changes.
  const defByIdRef = useRef<Map<string, MaterialDef>>(new Map());
  defByIdRef.current = new Map(props.materials.map((m) => [m.id, m]));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!props.active || !canvas) return;

    const renderer = new WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    const scene = new Scene();
    scene.background = new Color(0x8ec5ff);
    scene.fog = new Fog(0xbfe0ff, 40, 220);

    // --- Ground: a large dirt plane (soil colour from the material DB)
    // plus a dim earth-tone grid for a sense of scale + motion. ---
    const GROUND = 400;
    const groundGeo = new PlaneGeometry(GROUND, GROUND);
    groundGeo.rotateX(-Math.PI / 2);
    const groundMat = new MeshStandardMaterial({
      color: 0x6e5032,
      roughness: 0.95,
      metalness: 0.0,
    });
    const ground = new Mesh(groundGeo, groundMat);
    scene.add(ground);

    const grid = new GridHelper(GROUND, 200, 0x4a3520, 0x4a3520);
    (grid.material as Material).opacity = 0.25;
    (grid.material as Material).transparent = true;
    grid.position.y = 0.01;
    scene.add(grid);

    // --- Sun: warm directional key light + ambient fill + a visible
    // glowing sphere in the sky as the source. ---
    const sun = new DirectionalLight(0xfff2cc, 2.2);
    sun.position.set(60, 90, 40);
    scene.add(sun);
    scene.add(new AmbientLight(0x9fb8d8, 0.55));
    const sunBall = new Mesh(
      new SphereGeometry(8, 24, 24),
      new MeshBasicMaterial({ color: 0xfff4d0 }),
    );
    sunBall.position.copy(sun.position).multiplyScalar(1.6);
    scene.add(sunBall);

    // --- Player: the axolotl. Position + yaw are the avatar's; the
    // camera orbits behind it. The GLB loads async; until it's in we
    // still render + accept input (the camera just follows an empty
    // position). ---
    const player = new Vector3(0, 0, 0);
    let playerYaw = 0; // radians; 0 faces -z
    let playerVY = 0; // vertical velocity for jumping (units/sec)
    let axolotl: Object3D | null = null;
    let mixer: AnimationMixer | null = null;
    const clock = new Clock();
    const actions = new Map<string, AnimationAction>();
    let currentAction: AnimationAction | null = null;
    let modelScale = 1;

    const playAction = (keywords: string[]) => {
      if (actions.size === 0) return;
      let next: AnimationAction | undefined;
      for (const kw of keywords) {
        for (const [name, action] of actions) {
          if (name.includes(kw)) {
            next = action;
            break;
          }
        }
        if (next) break;
      }
      if (!next) next = actions.values().next().value;
      if (!next || next === currentAction) return;
      next.reset().fadeIn(0.2).play();
      currentAction?.fadeOut(0.2);
      currentAction = next;
    };

    const loader = new GLTFLoader();
    loader.load(MASCOT_GLB_URL, (gltf) => {
      const root = gltf.scene;
      // Normalise to a ~1.4-unit-tall creature standing on the ground.
      const box = new Box3().setFromObject(root);
      const size = box.getSize(new Vector3());
      const center = box.getCenter(new Vector3());
      const longest = Math.max(size.x, size.y, size.z) || 1;
      modelScale = 1.4 / longest;
      root.scale.setScalar(modelScale);
      // Recentre on origin, feet on the ground.
      root.position.sub(center.multiplyScalar(modelScale));
      root.position.y = -box.min.y * modelScale;
      const holder = new Group(); // yaw pivot for the model
      holder.add(root);
      scene.add(holder);
      axolotl = holder;
      if (gltf.animations.length > 0) {
        mixer = new AnimationMixer(root);
        gltf.animations.forEach((clip: AnimationClip) => {
          actions.set(clip.name.toLowerCase(), mixer!.clipAction(clip));
        });
        playAction(["idle", "float", "swim"]);
      }
    });

    // --- Camera: third-person follow. orbitYaw / orbitPitch are the
    // user's drag offsets layered on top of the player's facing. ---
    const camera = new PerspectiveCamera(60, 1, 0.1, 500);
    let orbitYaw = 0;
    let orbitPitch = 0.45;
    let followDist = 6;

    const place = () => {
      const ang = playerYaw + orbitYaw;
      const eye = new Vector3(
        player.x + followDist * Math.sin(ang) * Math.cos(orbitPitch),
        player.y + 1.0 + followDist * Math.sin(orbitPitch),
        player.z + followDist * Math.cos(ang) * Math.cos(orbitPitch),
      );
      camera.position.copy(eye);
      camera.lookAt(player.x, player.y + 0.7, player.z);
      if (axolotl) {
        axolotl.position.set(player.x, player.y, player.z);
        axolotl.rotation.y = playerYaw + Math.PI; // face travel dir
      }
    };

    const resize = () => {
      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    place();

    // --- Placement + reactions. Blocks are keyed by their integer
    // (x, y, z) cell so the reaction pass can find 6-neighbours; the
    // per-column stack height lets new blocks pile on top. ---
    interface Placed {
      obj: Mesh | InstancedMesh;
      id: string; // material id of this voxel
    }
    const stacks = new Map<string, number>(); // "x,z" → column height
    const cells = new Map<string, Placed>(); // "x,y,z" → placed voxel
    const raycaster = new Raycaster();
    const ndc = new Vector2();

    const cellKey = (x: number, y: number, z: number) => `${x},${y},${z}`;

    const addVoxel = (gx: number, gy: number, gz: number, def: MaterialDef | null) => {
      const kind = shapeForRef.current(def);
      const obj = buildPlacedObject(def, kind);
      obj.position.set(gx, gy + 0.5, gz);
      scene.add(obj);
      cells.set(cellKey(gx, gy, gz), { obj, id: def?.id ?? "empty" });
    };

    const removeVoxel = (key: string) => {
      const p = cells.get(key);
      if (!p) return;
      scene.remove(p.obj);
      p.obj.geometry.dispose();
      (p.obj.material as Material).dispose();
      cells.delete(key);
    };

    // --- Fine-grain powder field. Powders / grainy materials are NOT
    // stored as 1-unit voxels; each placed cell seeds F³ tiny grains
    // into a sub-cell grid that runs its own falling-sand CA, so
    // poured sand cascades and settles into a real low-angle pile of
    // granules instead of stacking as chunky cubes. All grains share
    // one InstancedMesh (one draw call). ---
    const F = 8; // grains per unit-cell edge → F³ grains per placed cell
    const GRAIN_CAP = 90000;
    const grainGeo = new SphereGeometry((0.5 / F) * 0.92, 5, 4);
    const grainMat = new MeshStandardMaterial({ roughness: 0.98, metalness: 0.0 });
    const grainMesh = new InstancedMesh(grainGeo, grainMat, GRAIN_CAP);
    grainMesh.count = 0;
    grainMesh.frustumCulled = false;
    scene.add(grainMesh);
    interface Grain {
      id: string;
      v: number; // fixed per-grain brightness so the pile speckles
    }
    const grains = new Map<string, Grain>(); // "FX,FY,FZ" → grain
    const grainTop = new Map<string, number>(); // "gx,gz" → world surface y
    let grainDirty = false;
    const fkey = (fx: number, fy: number, fz: number) => `${fx},${fy},${fz}`;
    const grainOcc = (fx: number, fy: number, fz: number) => grains.has(fkey(fx, fy, fz));
    const solidAtCoarse = (gx: number, gy: number, gz: number) => {
      const p = cells.get(cellKey(gx, gy, gz));
      return !!p && defByIdRef.current.get(p.id)?.phase === "solid";
    };
    const grainBlocked = (fx: number, fy: number, fz: number) =>
      grainOcc(fx, fy, fz) ||
      solidAtCoarse(Math.floor(fx / F), Math.floor(fy / F), Math.floor(fz / F));

    const spawnGrains = (gx: number, gz: number, def: MaterialDef | null) => {
      if (!def) return;
      // Drop the charge one cell above the current surface so it falls.
      let solidTop = 0;
      for (const [k, p] of cells) {
        if (defByIdRef.current.get(p.id)?.phase !== "solid") continue;
        const c = k.split(",");
        if (Number(c[0]) === gx && Number(c[2]) === gz) {
          solidTop = Math.max(solidTop, Number(c[1]) + 1);
        }
      }
      const surface = Math.max(solidTop, Math.ceil(grainTop.get(`${gx},${gz}`) ?? 0));
      const baseFY = (surface + 1) * F;
      for (let i = 0; i < F; i++) {
        for (let j = 0; j < F; j++) {
          for (let k = 0; k < F; k++) {
            if (grains.size >= GRAIN_CAP) break;
            const key = fkey(gx * F + i, baseFY + j, gz * F + k);
            if (!grains.has(key)) {
              grains.set(key, { id: def.id, v: 0.86 + Math.random() * 0.28 });
            }
          }
        }
      }
      grainDirty = true;
    };

    const GDIRS: [number, number][] = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
    let gParity = 0;
    const runGrainFlow = () => {
      gParity ^= 1;
      const dirs = gParity ? GDIRS : [...GDIRS].reverse();
      const keys = [...grains.keys()];
      keys.sort((a, b) => Number(a.split(",")[1]) - Number(b.split(",")[1]));
      let moved = false;
      for (const key of keys) {
        const gr = grains.get(key);
        if (!gr) continue;
        const c = key.split(",");
        const fx = Number(c[0]);
        const fy = Number(c[1]);
        const fz = Number(c[2]);
        if (fy <= 0) continue; // resting on the world floor
        // Straight down.
        if (!grainBlocked(fx, fy - 1, fz)) {
          grains.delete(key);
          grains.set(fkey(fx, fy - 1, fz), gr);
          moved = true;
          continue;
        }
        // Diagonal slide → angle of repose.
        for (const [dx, dz] of dirs) {
          if (
            !grainBlocked(fx + dx, fy - 1, fz + dz) &&
            !grainBlocked(fx + dx, fy, fz + dz)
          ) {
            grains.delete(key);
            grains.set(fkey(fx + dx, fy - 1, fz + dz), gr);
            moved = true;
            break;
          }
        }
      }
      if (moved) grainDirty = true;
    };

    const grainMtx = new Matrix4();
    const grainCol = new Color();
    const rebuildGrainMesh = () => {
      grainTop.clear();
      let i = 0;
      for (const [key, gr] of grains) {
        if (i >= GRAIN_CAP) break;
        const c = key.split(",");
        const fx = Number(c[0]);
        const fy = Number(c[1]);
        const fz = Number(c[2]);
        grainMtx.makeTranslation((fx + 0.5) / F - 0.5, (fy + 0.5) / F, (fz + 0.5) / F - 0.5);
        grainMesh.setMatrixAt(i, grainMtx);
        const base = defByIdRef.current.get(gr.id)?.color ?? [200, 180, 120];
        grainCol.setRGB((base[0] / 255) * gr.v, (base[1] / 255) * gr.v, (base[2] / 255) * gr.v);
        grainMesh.setColorAt(i, grainCol);
        i++;
        const ck = `${Math.floor(fx / F)},${Math.floor(fz / F)}`;
        const top = (fy + 1) / F;
        if (top > (grainTop.get(ck) ?? 0)) grainTop.set(ck, top);
      }
      grainMesh.count = i;
      grainMesh.instanceMatrix.needsUpdate = true;
      if (grainMesh.instanceColor) grainMesh.instanceColor.needsUpdate = true;
      grainDirty = false;
    };

    const placeBlockAtPointer = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      ndc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      // Hit the ground plane + any existing blocks + the grain field;
      // pick the nearest.
      const targets: Object3D[] = [
        ground,
        grainMesh,
        ...[...cells.values()].map((p) => p.obj),
      ];
      const hits = raycaster.intersectObjects(targets, false);
      if (hits.length === 0) return;
      const hit = hits[0];
      const gx = Math.round(hit.point.x);
      const gz = Math.round(hit.point.z);
      const key = `${gx},${gz}`;
      const def = materialRef.current;
      // Grainy materials pour into the fine-grain field; everything
      // else stacks as a 1-unit voxel.
      if (shapeForRef.current(def) === "grains") {
        spawnGrains(gx, gz, def);
        return;
      }
      const h = stacks.get(key) ?? 0;
      addVoxel(gx, h, gz, def);
      stacks.set(key, h + 1);
    };

    // Reaction pass: walk every placed voxel, and where a contact rule
    // matches one of its 6 neighbours, transform it (and drop the
    // secondary product into a free cell above). Runs on a timer, not
    // every frame, so the world reacts at a watchable pace and the
    // cost stays trivial. Uses the engine's own reaction rules.
    const NEIGHBOURS: [number, number, number][] = [
      [1, 0, 0],
      [-1, 0, 0],
      [0, 1, 0],
      [0, -1, 0],
      [0, 0, 1],
      [0, 0, -1],
    ];
    const runReactions = () => {
      const transforms: { key: string; become: string; spawn: string | null; pos: [number, number, number] }[] = [];
      for (const [key, p] of cells) {
        const [sx, sy, sz] = key.split(",").map(Number);
        for (const [dx, dy, dz] of NEIGHBOURS) {
          const nb = cells.get(cellKey(sx + dx, sy + dy, sz + dz));
          if (!nb) continue;
          const rule = CONTACT_RULES.get(`${p.id}|${nb.id}`);
          if (rule) {
            transforms.push({ key, become: rule.become, spawn: rule.spawn, pos: [sx, sy, sz] });
            break;
          }
        }
      }
      for (const t of transforms) {
        // The reactant may already have been consumed by an earlier
        // transform this pass; skip if it's gone.
        if (!cells.has(t.key)) continue;
        removeVoxel(t.key);
        const [sx, sy, sz] = t.pos;
        addVoxel(sx, sy, sz, defByIdRef.current.get(t.become) ?? null);
        if (t.spawn) {
          // Drop the secondary product into the cell above if it's free.
          const aboveKey = cellKey(sx, sy + 1, sz);
          if (!cells.has(aboveKey)) {
            addVoxel(sx, sy + 1, sz, defByIdRef.current.get(t.spawn) ?? null);
            const colKey = `${sx},${sz}`;
            stacks.set(colKey, Math.max(stacks.get(colKey) ?? 0, sy + 2));
          }
        }
      }
    };
    let reactionTimer = 0;

    // --- Avatar collision against placed blocks. A material blocks
    // movement when its phase is solid (block / grains / crystal);
    // liquids and gases are passable. `columnTop` returns the height
    // the avatar stands at over column (gx, gz) — the top face of the
    // tallest solid voxel there, or 0 for bare ground. ---
    const isSolidId = (id: string) => defByIdRef.current.get(id)?.phase === "solid";
    const columnTop = (gx: number, gz: number): number => {
      let top = 0;
      for (const [key, p] of cells) {
        if (!isSolidId(p.id)) continue;
        const c = key.split(",");
        if (Number(c[0]) === gx && Number(c[2]) === gz) {
          const cyTop = Number(c[1]) + 1;
          if (cyTop > top) top = cyTop;
        }
      }
      return top;
    };
    // The surface the avatar walks on at (gx, gz): the higher of the
    // solid stack top and the sand-pile surface, so it stands on both.
    const groundHeight = (gx: number, gz: number): number =>
      Math.max(columnTop(gx, gz), grainTop.get(`${gx},${gz}`) ?? 0);

    // --- Liquid + powder flow. A lightweight 3D cellular pass so
    // placed water falls, spreads downhill, and pools when contained;
    // powders (sand…) fall straight + slide diagonally into heaps.
    // Runs on a fast timer (not every frame) and only over the movable
    // voxels, so it stays cheap. ---
    const isLiquidId = (id: string) => defByIdRef.current.get(id)?.phase === "liquid";
    const isPowderId = (id: string) => defByIdRef.current.get(id)?.behavior === "powder";
    const occupied = (x: number, y: number, z: number) => cells.has(cellKey(x, y, z));
    const liquidAt = (x: number, y: number, z: number) => {
      const p = cells.get(cellKey(x, y, z));
      return !!p && isLiquidId(p.id);
    };
    const moveVoxel = (fromKey: string, tx: number, ty: number, tz: number) => {
      const p = cells.get(fromKey);
      if (!p) return;
      cells.delete(fromKey);
      cells.set(cellKey(tx, ty, tz), p);
      p.obj.position.set(tx, ty + 0.5, tz);
    };
    const HSTEPS: [number, number][] = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
    let flowParity = 0;
    const runFlow = () => {
      flowParity ^= 1;
      const order = flowParity ? HSTEPS : [...HSTEPS].reverse();
      // Lowest voxels first so a falling cell isn't processed twice.
      const movable = [...cells.keys()].filter((k) => {
        const id = cells.get(k)!.id;
        return isLiquidId(id) || isPowderId(id);
      });
      movable.sort((a, b) => Number(a.split(",")[1]) - Number(b.split(",")[1]));
      for (const key of movable) {
        const p = cells.get(key);
        if (!p) continue;
        const parts = key.split(",");
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        const z = Number(parts[2]);
        // Fall straight down into empty space (floor is y = 0).
        if (y > 0 && !occupied(x, y - 1, z)) {
          moveVoxel(key, x, y - 1, z);
          continue;
        }
        if (isPowderId(p.id)) {
          // Slide diagonally down into a heap.
          let moved2 = false;
          for (const [dx, dz] of order) {
            if (y > 0 && !occupied(x + dx, y - 1, z + dz) && !occupied(x + dx, y, z + dz)) {
              moveVoxel(key, x + dx, y - 1, z + dz);
              moved2 = true;
              break;
            }
          }
          if (moved2) continue;
          // Slope-of-repose: if this grain sits on top of a powder
          // column that's taller than its neighbour's by more than one
          // (i.e. a near-vertical face of a pile), topple sideways
          // onto the lower column. This naturally levels a placed
          // sand pillar into a gently sloped mound instead of a
          // tower.
          const colHere = columnTop(x, z); // top of solid stack at our column
          for (const [dx, dz] of order) {
            const colN = columnTop(x + dx, z + dz);
            if (colHere - colN >= 2 && !occupied(x + dx, y, z + dz)) {
              moveVoxel(key, x + dx, y, z + dz);
              break;
            }
          }
          continue;
        }
        // Liquid: flow toward an adjacent drop (downhill).
        let flowed = false;
        for (const [dx, dz] of order) {
          if (!occupied(x + dx, y, z + dz) && y > 0 && !occupied(x + dx, y - 1, z + dz)) {
            moveVoxel(key, x + dx, y, z + dz);
            flowed = true;
            break;
          }
        }
        if (flowed) continue;
        // Pressure relief: if liquid is stacked above, spread sideways
        // into any empty neighbour so it levels out instead of towering
        // (two waters atop each other flow apart); a walled basin with
        // nothing above settles into a still pool.
        if (liquidAt(x, y + 1, z)) {
          for (const [dx, dz] of order) {
            if (!occupied(x + dx, y, z + dz)) {
              moveVoxel(key, x + dx, y, z + dz);
              break;
            }
          }
        }
      }
    };
    let flowTimer = 0;

    // --- Input ---
    let dragging = false;
    let moved = false;
    let lastX = 0;
    let lastY = 0;
    const DRAG_THRESHOLD = 4;
    const held = new Set<string>();

    const onDown = (e: PointerEvent) => {
      dragging = true;
      moved = false;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      if (Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) moved = true;
      orbitYaw -= dx * 0.006;
      orbitPitch = Math.min(1.4, Math.max(0.05, orbitPitch - dy * 0.005));
      lastX = e.clientX;
      lastY = e.clientY;
      place();
    };
    const onUp = (e: PointerEvent) => {
      dragging = false;
      canvas.releasePointerCapture(e.pointerId);
      // A click (no meaningful drag) places a block.
      if (!moved) placeBlockAtPointer(e.clientX, e.clientY);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      followDist = Math.min(20, Math.max(2.5, followDist * (1 + e.deltaY * 0.001)));
      place();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onExitRef.current();
        return;
      }
      if (e.key.startsWith("Arrow") || e.key === " ") {
        held.add(e.key);
        e.preventDefault();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => held.delete(e.key);

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("resize", resize);

    const GRAVITY = 22; // units/s²
    const JUMP_V = 8.5; // initial jump velocity
    let rafId = 0;
    const loop = () => {
      const dt = clock.getDelta();
      // Is the avatar wading / submerged? (liquid at its cell).
      const acx = Math.round(player.x);
      const acz = Math.round(player.z);
      const afy = Math.floor(player.y);
      const inLiquid = liquidAt(acx, afy, acz) || liquidAt(acx, afy + 1, acz);
      // Arrow-key locomotion (walk anywhere on the ground plane,
      // blocked by solid blocks; can step up onto a single one).
      // Liquid drags movement to a slow wade.
      let moving = false;
      const turnSpeed = 2.2 * dt;
      const moveSpeed = (inLiquid ? 3.0 : 6.0) * dt;
      const STEP_UP = 1.1; // tallest ledge the avatar can walk onto
      if (held.has("ArrowLeft")) playerYaw += turnSpeed;
      if (held.has("ArrowRight")) playerYaw -= turnSpeed;
      if (held.has("ArrowUp") || held.has("ArrowDown")) {
        const dir = held.has("ArrowUp") ? 1 : -1;
        // Forward is -z rotated by yaw.
        const nx = player.x - Math.sin(playerYaw) * moveSpeed * dir;
        const nz = player.z - Math.cos(playerYaw) * moveSpeed * dir;
        // Only enter the target column if the solid stack there is low
        // enough to step / jump onto; otherwise it's a wall.
        const targetTop = groundHeight(Math.round(nx), Math.round(nz));
        if (targetTop - player.y <= STEP_UP) {
          player.x = nx;
          player.z = nz;
        }
        moving = true;
      }
      // Jump + gravity. The floor under the avatar is the top of the
      // solid stack in its column (so it stands on placed blocks and
      // can jump off them); bare ground is 0.
      const floorY = groundHeight(Math.round(player.x), Math.round(player.z));
      const grounded = player.y <= floorY + 0.001 && playerVY <= 0;
      if (inLiquid) {
        // Swimming: buoyancy floats the avatar up, damped gravity,
        // Space paddles upward; vertical speed stays gentle so it
        // bobs rather than rockets.
        if (held.has(" ")) playerVY = 4.2;
        else {
          playerVY -= GRAVITY * 0.25 * dt;
          playerVY += 6.5 * dt; // buoyancy
        }
        playerVY = Math.max(-3, Math.min(5, playerVY));
      } else {
        if (held.has(" ") && grounded) playerVY = JUMP_V;
        playerVY -= GRAVITY * dt;
      }
      player.y += playerVY * dt;
      if (player.y < floorY) {
        player.y = floorY;
        playerVY = 0;
      }
      const airborne = !inLiquid && player.y > floorY + 0.05;
      place();
      // Animation: swim in liquid, jump while airborne, walk while
      // moving, idle otherwise.
      if (mixer) {
        if (inLiquid) playAction(["swim", "float", "idle"]);
        else if (airborne) playAction(["jump", "swim", "walk"]);
        else if (moving) playAction(["walk", "move", "run", "swim"]);
        else playAction(["idle", "float"]);
        mixer.update(dt);
      }
      // Liquid flow + fine-grain (sand) cascade on a fast cadence.
      flowTimer += dt;
      if (flowTimer >= 0.06) {
        flowTimer = 0;
        runFlow();
        runGrainFlow();
        if (grainDirty) rebuildGrainMesh();
      }
      // Contact reactions on a ~0.35 s cadence.
      reactionTimer += dt;
      if (reactionTimer >= 0.35) {
        reactionTimer = 0;
        runReactions();
      }
      resize();
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("resize", resize);
      mixer?.stopAllAction();
      for (const p of cells.values()) {
        p.obj.geometry.dispose();
        (p.obj.material as Material).dispose();
      }
      grainGeo.dispose();
      grainMat.dispose();
      groundGeo.dispose();
      groundMat.dispose();
      grid.geometry.dispose();
      (grid.material as Material).dispose();
      sunBall.geometry.dispose();
      (sunBall.material as Material).dispose();
      renderer.dispose();
    };
  }, [props.active]);

  if (!props.active) return null;
  return (
    <div className="full3d-host">
      <canvas ref={canvasRef} className="full3d-view" />
      <button
        type="button"
        className="full3d-exit"
        onClick={props.onExit}
        title="Leave Full 3D Mode (Esc)"
      >
        ✕ Exit Full 3D Mode
      </button>
      <div className="full3d-hint">
        Arrows move · Space jump / swim · click to place · drag to look —
        water flows &amp; pools, materials react (try water + lava)
      </div>
    </div>
  );
}
